import type { Ring, Vec2 } from '../../../../geo/types';
import { beamSolid, horizontalCap, planPoint } from '../../solids';
import { panelWall } from '../../panels';
import { hipRoof } from '../../roofSolids';
import { edgePanel, openedWall, rectWall } from '../masonry';
import { arcadeOutline, archPath, archSoffits, type Arch } from '../arcade';
import { pinnacleSolid } from '../gothic/pinnacle';
import { stringCourse } from '../gothic/bands';
import { gothic, PINNACLE, pointedAt, type StadhuisBuild } from './stadhuisParts';
import { buildVierschaarOrnaments } from './stadhuisOrnaments';

/**
 * The upper windows of the vierschaar: one pointed window over each arch, as the woodcut draws
 * tall openings over the arcade (stadhuis-woodcut.jpg); their size is not recorded.
 */
const UPPER = { width: 1.2, sill: 8.2, top: 11.6, bayBehind: 5.5 } as const;

/** A string course over the arcade and a cornice under the eaves. */
const MOULDING = {
  course: { height: 0.3, outset: 0.1 },
  cornice: { height: 0.35, outset: 0.15 },
} as const;

/* An arch cut through a wall, built on its outer face and on the loggia face behind it. */
interface Pierced {
  readonly outer: readonly [Vec2, Vec2];
  readonly inner: readonly [Vec2, Vec2];
  readonly outerArches: readonly Arch[];
  readonly innerArches: readonly Arch[];
  readonly windows: readonly Ring[];
}

/** The front and the two sides of the projection, each with its arches and windows. */
function piercedWalls(s: StadhuisBuild): Pierced[] {
  const { vierschaar: vs, loggia: lg, arcade, sideArch } = s.plan;
  const W = vs.u1;
  const D = vs.v1;
  const behind = D - lg.v1;
  const count = Math.max(1, Math.round(behind / UPPER.bayBehind));
  const southWindows = Array.from({ length: count }, (_, k) =>
    pointedAt(((k + 0.5) * behind) / count, UPPER.width, UPPER.sill, UPPER.top)
  );
  const over = (at: number): Ring => pointedAt(at, UPPER.width, UPPER.sill, UPPER.top);
  return [
    {
      outer: [
        [0, 0],
        [W, 0],
      ],
      inner: [
        [lg.u1, lg.v0],
        [lg.u0, lg.v0],
      ],
      outerArches: arcade.map((a) => gothic(a)),
      innerArches: arcade.map((a) => gothic(a, lg.u1 - a.center)),
      windows: arcade.map((a) => over(a.center)),
    },
    {
      outer: [
        [0, D],
        [0, 0],
      ],
      inner: [
        [lg.u0, lg.v0],
        [lg.u0, lg.v1],
      ],
      outerArches: [gothic(sideArch, D - sideArch.center)],
      innerArches: [gothic(sideArch, sideArch.center - lg.v0)],
      windows: [...southWindows, over(D - sideArch.center)],
    },
    {
      outer: [
        [W, 0],
        [W, D],
      ],
      inner: [
        [lg.u1, lg.v1],
        [lg.u1, lg.v0],
      ],
      outerArches: [gothic(sideArch)],
      innerArches: [gothic(sideArch, lg.v1 - sideArch.center)],
      windows: [over(sideArch.center)],
    },
  ];
}

function length(edge: readonly [Vec2, Vec2]): number {
  return Math.hypot(edge[1][0] - edge[0][0], edge[1][1] - edge[0][1]);
}

function buildPierced(s: StadhuisBuild, wall: Pierced): void {
  const { vierschaar: vs, loggia: lg } = s.plan;
  const outline = arcadeOutline(length(wall.outer), vs.eaveY, wall.outerArches);
  openedWall(
    s.builder,
    s.stone,
    s.frame,
    edgePanel(wall.outer[0], wall.outer[1]),
    outline,
    wall.windows
  );
  archSoffits(s.builder, s.stone.wall, s.frame, wall.outer, lg.v0, wall.outerArches);
  // A shallow stone moulding makes the painted arcade's edges readable at eye level.
  // Its profile is an estimate; it leaves the existing openings and walking route intact.
  const [a, b] = wall.outer,
    span = length(wall.outer);
  const dx = (b[0] - a[0]) / span,
    dy = (b[1] - a[1]) / span;
  const at = ([x, y]: Vec2) => planPoint(a[0] + dx * x + dy * 0.05, y, a[1] + dy * x - dx * 0.05);
  for (const arch of wall.outerArches) {
    const rim = archPath(arch);
    for (let i = 1; i < rim.length; i++)
      beamSolid(s.builder, s.stone.wall, s.frame, at(rim[i - 1]!), at(rim[i]!), 0.065);
  }
  /* The reference prints clarify moulded window surrounds. This shallow estimated
     profile follows the existing pointed openings without changing their positions. */
  for (const window of wall.windows)
    window.forEach((point, i) => {
      beamSolid(
        s.builder,
        s.stone.wall,
        s.frame,
        at(point),
        at(window[(i + 1) % window.length]!),
        0.09
      );
    });
  const inner = arcadeOutline(length(wall.inner), lg.ceilingY, wall.innerArches);
  panelWall(s.builder, s.dark, s.frame, edgePanel(wall.inner[0], wall.inner[1]), inner, []);
}

/** The shaded loggia behind the arches: its back wall on the building line and its ceiling. */
function buildLoggia(s: StadhuisBuild): void {
  const { u0, u1, v0, v1, ceilingY } = s.plan.loggia;
  rectWall(
    s.builder,
    s.dark,
    s.frame,
    [
      [u0, v1],
      [u1, v1],
    ],
    [0, ceilingY]
  );
  const ceiling: Ring = [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];
  horizontalCap(s.builder, s.dark.wall, s.frame, ceiling, ceilingY, false);
}

/** Pinnacles on the two front corners, their tips level with the hip's apex as painted. */
function buildPinnacles(s: StadhuisBuild): void {
  const { u1, eaveY } = s.plan.vierschaar;
  const p = PINNACLE;
  for (const u of [p.inset, u1 - p.inset]) {
    const spec = {
      center: [u, p.inset] as const,
      half: p.half,
      y0: eaveY - p.sink,
      shaftTop: eaveY + p.shaft,
      tipY: eaveY + p.tip,
    };
    pinnacleSolid(s.builder, s.stone.wall, s.frame, spec);
  }
}

/**
 * The vierschaar: an open arcade of three pointed arches on the Dam and one in each side of the
 * projection, the court's upper floor over it and a steep slate hip behind a pinnacled front.
 */
export function buildVierschaar(s: StadhuisBuild): void {
  const vs = s.plan.vierschaar;
  piercedWalls(s).forEach((wall) => {
    buildPierced(s, wall);
  });
  buildLoggia(s);
  rectWall(
    s.builder,
    s.stone,
    s.frame,
    [
      [vs.u1, vs.v1],
      [vs.u0, vs.v1],
    ],
    [0, vs.eaveY]
  );
  hipRoof(s.builder, s.stone.roof, s.frame, {
    u0: vs.u0,
    u1: vs.u1,
    v0: vs.v0,
    v1: vs.v1,
    eaveY: vs.eaveY,
    pitch: vs.pitch,
  });
  stringCourse(s.builder, s.stone.wall, s.frame, vs, {
    y: s.plan.loggia.ceilingY,
    ...MOULDING.course,
  });
  stringCourse(s.builder, s.stone.wall, s.frame, vs, {
    y: vs.eaveY - MOULDING.cornice.height,
    ...MOULDING.cornice,
  });
  buildPinnacles(s);
  buildVierschaarOrnaments(s);
}
